import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import "./landing/landing.css";
import ParcelMap from "./landing/ParcelMap";
import {
  LISTINGS, equity, fmtMoney, minNextUpset, COUNTIES, STATEWIDE_NOTICE_COUNTIES,
  LEAD_CATEGORIES, ADDRESSED_LEADS, countySlug,
} from "./landing/landingData";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";
import { useTrackedPropertiesCount } from "../hooks/useTrackedPropertiesCount";
import { useAddressedLeadsCount } from "../hooks/useAddressedLeadsCount";
import { usePublicCountyStats } from "../hooks/usePublicCountyStats";

interface LandingPageProps {
  onSignIn: () => void;
  onGetStarted: () => void;
}

// Editorial landing (Playfair/blue design, imported from Claude-design 51d58388,
// 2026-06-25). Ported Tailwind -> scoped vanilla CSS under .lp. Honest copy:
// tiered coverage claim (COUNTY_COUNT deep-detail counties, statewide notice
// reach across STATEWIDE_NOTICE_COUNTIES — see landingData.ts's block comment
// for why "never 100" was the old rule and doesn't apply to the notice tier),
// updated twice a day, EMAIL alerts only (SMS scrubbed -> #616), credible-
// generic sources (never names our scrapers/APIs/ firms), real Mecklenburg
// listings. Dropped the design's dev "sections panel" and the live ticker;
// nav Pricing -> the /pricing page.
//
// 2026-07-28 (#875) — repositioned from "NC tax-foreclosure tracker" to
// OFF-MARKET LEAD GENERATOR. Tax foreclosure was 3.7% of our leads and 100% of
// our branding; probate (the largest addressed category) was invisible. Rules
// this file must keep honoring:
//   - Headline the ADDRESSED leads + tracked properties, never the raw 9,236
//     notice count (87% of it is name-and-case-number only).
//   - Never advertise divorce leads — 550 records, zero addresses.
//   - Probate's 629 addresses are CONTACT addresses (the estate rep), not
//     property addresses. Say which is which; do not merge the labels.
//   - Coverage is TIERED — parcel depth and notice reach are separate
//     claims and the copy must not blur them. The deep-parcel counties
//     (≥25 parcel_facts rows) are now LIVE-fetched from /public/county-stats'
//     deep_counties — ~77 as of 2026-09-14, was a hardcoded 30 that rotted —
//     and rendered as links to /public/<county>. They are the
//     deep-detail tier — assessed value, equity, upset-bid history,
//     owner. The notice-lead pipeline (NCNotices + Column + Tyler
//     Portal) reaches STATEWIDE_NOTICE_COUNTIES = 100 — every NC
//     county's foreclosure filings. Say both numbers explicitly and
//     never let one stand in for the other. Bridge with "more counties
//     getting deeper coverage over time" — the founder's own framing.
//     See #1663 and docs/adr/0019-*.md §6.
// Counts live in landing/landingData.ts with their provenance.

const M = LISTINGS[0]; // Meadowdale (featured)
const A = LISTINGS[1]; // Albemarle
const D = LISTINGS[2]; // Madrid

// "Alerts when a bid moves" was here and claimed true — alerts are built
// but have delivered 0 emails since 2026-07-21, so that row was false on
// the live product. Swapped for a claim that's actually true today: CSV
// export exists (Pro-gated, /leads/export + /notice-leads/export) and
// isn't offered by eCourts or the national sites either.
const COMPARE: [string, boolean, boolean, boolean][] = [
  ["Foreclosure, probate, tax & HOA in one feed", true, false, false],
  ["Owners reachable months before a listing", true, false, false],
  ["Tracks every upset reset for you", true, false, false],
  ["CSV export for mail-merge", true, false, false],
  ["No CVD / case number needed", true, false, true],
  ["Tells you which leads have an address", true, false, false],
];

// FAQS used to be a plain module constant, which is why the leads-count
// answer below hardcoded ADDRESSED_LEADS (the frozen 2026-08-16 snapshot,
// 1,891) instead of the live count the hero headline already uses. That
// meant the hero could say "25,953 leads carry an address" while this FAQ
// answer, a few screens down on the SAME page, said "1,891" — a number the
// hook's own doc comment says understates reality by roughly half, and
// which had drifted to over 13x off by 2026-09. Now a function of the live
// value, called from inside the component after the hook has run, so it
// can never diverge from the headline again.
function buildFaqs(addressedLeads: number, deepCountyCount: number): [string, React.ReactNode][] {
  return [
    ["What do you mean by \"off-market\"?", "Distressed and pre-market property owners, sourced from public legal notices before they list. A foreclosure filing, an estate opened with the clerk, a tax or HOA lien — each one names an owner whose situation usually ends in a sale. It is not pocket listings, and it is not an MLS feed. There is no agent on the other end yet, which is the whole point."],
    ["How many of these leads actually have an address?", <>Right now, {addressedLeads.toLocaleString()} of them. We are open about this because it is the number that decides whether the product is useful to you: a notice with only a name and a case number is not mailable and not driveable. Every lead in the app is labeled with what it carries, and you can filter to addressed-only before you export a single row.</>],
    ["Isn't this all free on eCourts?", "The records are public — but eCourts makes you know the case number, search one county at a time, and re-check every case by hand. Parcely reads the notices as they post, joins them to the parcel record where it can, and emails you when something in your buy box moves. You're paying for the watching, not the records."],
    ["Which counties do you cover?", <>Two answers, so here are both. Full parcel-level detail — assessed value, equity, upset-bid tracking — runs across {deepCountyCount} NC counties today, anchored on Mecklenburg, Guilford, Wake, Cumberland, Rowan and Gaston and expanding. The legal-notice feed (foreclosure filings, estate and probate, tax and HOA) reaches all {STATEWIDE_NOTICE_COUNTIES}, though notice depth outside the parcel-enriched counties is thinner. More counties keep getting deeper coverage over time; tell me where you invest and I'll prioritize it.</>],
    ["How fresh is the data?", "The docket refreshes twice a day, and every record is stamped with its source county and pull time. During an active upset window, that twice-daily check is what catches resets before the deadline."],
    ["What about title insurance on foreclosure purchases?", "Title on foreclosure purchases can be harder to insure, and some insurers want a quiet-title action first. Parcely helps you find and evaluate parcels — it isn't title work. Always run title and talk to a closing attorney before you commit."],
    ["Can I get alerts?", "Yes, on every plan. Properties on your watchlist email you when their status changes, when a new upset bid lands, and as the upset window closes, and a weekly digest rounds up your county. Free accounts get up to 5 new-listing alerts every 30 days; Scout ($45/mo, 3 counties you choose) and Pro ($75/mo, every NC county) have no cap. Browsing is free with a quick signup."],
  ];
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignIn, onGetStarted }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [expandMsg, setExpandMsg] = useState("");
  // Fallback to ~1900 while loading/error. This is a reasonable approximation
  // that will drift slightly as data grows; the live value is fetched in parallel.
  // `loading` is deliberately not destructured: the ?? fallback below covers the
  // loading, error, and empty cases identically, so there is nothing for a
  // loading flag to change. Destructuring it unused fails the CI build, since
  // react-scripts promotes lint warnings to errors when CI=true.
  const { count: trackedPropertiesCount } = useTrackedPropertiesCount();
  const displayTrackedProperties = trackedPropertiesCount ?? 1900;

  // #1560: hero headline `addressed leads` number was drifting ~50% below live
  // reality when hardcoded off the LEAD_CATEGORIES sum. Switched to a live
  // fetch of `leads_total` from `/public/county-stats` (#1562), falling back
  // to the LEAD_CATEGORIES sum (`ADDRESSED_LEADS`) on loading/error/omission.
  // Same rationale as `useTrackedPropertiesCount` above for skipping the
  // `loading` flag: the ?? fallback covers every non-success case identically.
  const { count: addressedLeadsCount } = useAddressedLeadsCount();
  const displayAddressedLeads = addressedLeadsCount ?? ADDRESSED_LEADS;

  // Live landing coverage: per-category lead numbers and the full-parcel-depth
  // county list from /public/county-stats. Falls back to the hardcoded
  // landingData snapshot when the fetch hasn't resolved or fails — same
  // discipline as the hero number above, so a marketing "truth page" can't
  // silently drift (the hardcoded category numbers had gone ~4 weeks + 12x
  // stale, and the county count read 30 when the real deep tier was 77).
  const { leadCategories: liveCats, deepCounties } = usePublicCountyStats();
  const coverageCounties =
    deepCounties && deepCounties.length > 0
      ? deepCounties.map((d) => ({ name: d.county_name, slug: d.county_id }))
      : COUNTIES.map((name) => ({ name, slug: countySlug(name) }));
  const coverageCount = coverageCounties.length;
  // Built from the SAME live-or-fallback value the hero headline uses, so
  // the FAQ answer can't drift from it the way the old hardcoded version did.
  const faqs = buildFaqs(displayAddressedLeads, coverageCount);

  useEffect(() => {
    const els = rootRef.current?.querySelectorAll<HTMLElement>(".lp-reveal");
    if (!els || !els.length) return;
    if (typeof IntersectionObserver === "undefined") { els.forEach((e) => e.classList.add("lp-shown")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("lp-shown"); io.unobserve(e.target); } });
    }, { threshold: 0.08 });
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  const handleExpand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    const { error } = await supabase.from("early_access").insert({ email });
    setExpandMsg(error ? "Couldn't save that — try again." : "Got it — I'll let you know when your county is live.");
    if (!error) setEmail("");
  };

  return (
    <div className="lp" ref={rootRef}>
      {/* NAV — shared editorial chrome (same header on every public page) */}
      <PublicPageHeader onSignIn={onSignIn} onGetStarted={onGetStarted} />

      <main id="main" tabIndex={-1}>
        {/* HERO */}
        <section id="sec-hero" className="lp-sec">
          <div className="lp-hero-inner">
            <div className="lp-reveal">
              <p className="lp-kicker">North Carolina Off-Market Property Leads</p>
              <h1 className="lp-h1 lp-display">Reach the owner before the sign goes up.</h1>
              <p className="lp-hero-lede">Parcely reads North Carolina's public legal notices — foreclosure, estate, tax and HOA filings — and turns them into owners you can actually contact. {displayAddressedLeads.toLocaleString()} leads carry an address today, and {displayTrackedProperties.toLocaleString()} parcels are tracked all the way through the sale and the upset-bid window.</p>
              <div className="lp-cta-row">
                <button className="lp-btn lp-btn-primary" onClick={onGetStarted}>Get started — free</button>
                <a href="#sec-categories" className="lp-btn lp-btn-outline">See what's in the feed</a>
              </div>
              <p className="lp-hero-trust">Updated twice a day · {coverageCount} counties with parcel-level detail · sourced from the public record</p>
            </div>
            <div className="lp-reveal"><ParcelMap /><p className="lp-map-cap">Stylized parcel view — click a pin to inspect the property.</p></div>
          </div>
        </section>

        {/* STATS */}
        <section id="sec-stats" className="lp-sec band">
          <div className="lp-stats">
            <div className="lp-stat"><p className="lp-stat-num lp-display">{displayAddressedLeads.toLocaleString()}</p><p className="lp-stat-label">LEADS WITH AN ADDRESS ON FILE</p></div>
            <div className="lp-stat"><p className="lp-stat-num lp-display">{displayTrackedProperties.toLocaleString()}</p><p className="lp-stat-label">PARCELS TRACKED THROUGH SALE</p></div>
            <div className="lp-stat"><p className="lp-stat-num lp-display">{coverageCount}</p><p className="lp-stat-label">COUNTIES · PARCEL-LEVEL DETAIL</p></div>
            <div className="lp-stat"><p className="lp-stat-num lp-display">7<span className="sm">am</span> &amp; 7<span className="sm">pm</span></p><p className="lp-stat-label">DOCKET REFRESH, DAILY</p></div>
          </div>
        </section>

        {/* SOURCES (genericized) */}
        <section id="sec-sources" className="lp-sec">
          <div className="lp-sources">
            <span className="lp-kicker lp-muted">Sourced from</span>
            <span className="lp-mono">public legal notices</span><span className="sep">·</span>
            <span className="lp-mono">court &amp; foreclosure filings</span><span className="sep">·</span>
            <span className="lp-mono">estate &amp; probate filings</span><span className="sep">·</span>
            <span className="lp-mono">property &amp; parcel records</span>
          </div>
        </section>

        {/* LEAD CATEGORIES — what "off-market" actually means here, and the
            honest per-category inventory. Numbers come from landingData.ts,
            which documents the exact prod columns behind each one (#875). */}
        <section id="sec-categories" className="lp-sec band">
          <div className="lp-pad-lg">
            <div className="lp-reveal" style={{ maxWidth: 680 }}>
              <p className="lp-kicker">The feed</p>
              <h2 className="lp-h2 lp-display">Four ways a house comes loose.</h2>
              <p className="lp-lede" style={{ fontSize: 14, marginTop: 16 }}>
                Off-market, here, means one thing: distressed and pre-market property owners sourced from public
                legal notices before they list. No pocket listings, no MLS feed, no agent on the other end — just
                the filings that usually end in a sale, read the day they post.
              </p>
            </div>
            <div className="lp-grid4 lp-reveal">
              {LEAD_CATEGORIES.map((c) => {
                const live = liveCats?.[c.key];
                const addressed = live?.addressed ?? c.addressed;
                const total = live?.total ?? c.total;
                return (
                  <div className="lp-cell" key={c.label}>
                    <p className="n">{addressed.toLocaleString()}</p>
                    <h3>{c.label}</h3>
                    <p>{c.blurb}</p>
                    <p className="lp-fineprint">
                      of {total.toLocaleString()} filings tracked · {c.addressKind}
                    </p>
                  </div>
                );
              })}
            </div>
            <p className="lp-fineprint lp-reveal" style={{ marginTop: 18, maxWidth: "58em" }}>
              The big number on each card is what carries an address today — the only kind you can mail or drive.
              Notices that arrive with a name and a case number only are labeled that way in the app and are not
              counted here. Probate addresses reach the estate's representative, not the property.
            </p>
          </div>
        </section>

        {/* F01 — DOCKET */}
        <section id="sec-f01" className="lp-sec">
          <div className="lp-feat lp-reveal">
            <div className="lp-feat-copy">
              <p className="lp-kicker">01 — When a lead goes to auction</p>
              <h2 className="lp-h2 lp-display">Every notice of sale, in one feed.</h2>
              <p>A lead you didn't reach in time becomes a lead you can bid on. Sale dates, opening bids, trustee or commissioner, file number, and parcel — read off the clerk of court and the trustee firms across all {coverageCount} parcel-enriched counties. No CVD number to look up, no {coverageCount} separate county pages to refresh.</p>
              <ul className="lp-feat-list">
                <li><span className="d">—</span> Filter by county, opening bid, or assessed-value band</li>
                <li><span className="d">—</span> Continuances and cancellations flagged automatically</li>
              </ul>
            </div>
            <div className="lp-feat-visual">
              <div className="lp-card">
                <div className="lp-card-head band"><span className="lp-mono">UPCOMING SALES — CHARLOTTE METRO</span><span className="lp-mono">Next 30 days</span></div>
                <div className="lp-table-scroll">
                {/* Was hardcoded absolute dates (Jul 01/08/09/14, 2026) with
                    no illustrative disclaimer — unlike the leads table below,
                    which correctly labels itself "Sample owner names shown."
                    Anyone viewing this after July saw a table titled
                    "Next 30 days" showing sale dates from months ago, which
                    is a checkably false claim, not just stale-looking. Day
                    offsets never go stale the way a fixed calendar date does. */}
                <table className="lp-table lp-tnum">
                  <thead><tr><th>Sale date</th><th>County</th><th>Address</th><th style={{ textAlign: "right" }}>Opening bid</th></tr></thead>
                  <tbody>
                    <tr><td className="lp-mono">in 3 days</td><td>Mecklenburg</td><td className="lp-muted">{M.address}</td><td style={{ textAlign: "right", fontWeight: 500 }}>{fmtMoney(M.opening)}</td></tr>
                    <tr><td className="lp-mono">in 8 days</td><td>Mecklenburg</td><td className="lp-muted">{A.address}</td><td style={{ textAlign: "right", fontWeight: 500 }}>{fmtMoney(A.opening)}</td></tr>
                    <tr><td className="lp-mono">in 9 days</td><td>Mecklenburg</td><td className="lp-muted">{D.address}</td><td style={{ textAlign: "right", fontWeight: 500 }}>{fmtMoney(D.opening)}</td></tr>
                    <tr><td className="lp-mono">in 14 days</td><td>Cabarrus</td><td className="lp-muted">Continued — see docket</td><td style={{ textAlign: "right" }} className="lp-pill-amber">CONTINUED</td></tr>
                  </tbody>
                </table>
                </div>
              </div>
              <p className="lp-fineprint">Illustrative sales, not a live feed — open the app for today's calendar.</p>
            </div>
          </div>
        </section>

        {/* F02 — UPSET CLOCK */}
        <section id="sec-f02" className="lp-sec band">
          <div className="lp-feat rev lp-reveal">
            <div className="lp-feat-copy">
              <p className="lp-kicker">02 — The upset clock</p>
              <h2 className="lp-h2 lp-display">Never miss an upset reset.</h2>
              <p>NC's 10-day upset-bid window is where deals are won and lost — and the clock restarts with every qualifying raise. Parcely logs each bid, recomputes the minimum next bid (the greater of 5% or $750), and resets the countdown when a new bid lands.</p>
              <ul className="lp-feat-list">
                <li><span className="d">—</span> Clock resets and deadline extensions tracked for you</li>
                <li><span className="d">—</span> Pushed to your alerts within hours of the filing</li>
              </ul>
            </div>
            <div className="lp-feat-visual">
              <div className="lp-card">
                <div className="lp-card-head"><span className="lp-mono">EXAMPLE · UPSET-BID LEDGER — {M.address.toUpperCase()}</span><span className="lp-mono lp-amber">10-day clock · 6d left</span></div>
                <div className="lp-ledger">
                  <div className="lp-ledger-clock">
                    <svg width="118" height="118" viewBox="0 0 118 118"><circle cx="59" cy="59" r="50" fill="none" stroke="#E3E7EC" strokeWidth="9" /><circle cx="59" cy="59" r="50" fill="none" stroke="#2E5A88" strokeWidth="9" strokeLinecap="round" strokeDasharray="314" strokeDashoffset="189" transform="rotate(-90 59 59)" /></svg>
                    <p className="d">DAY 4 OF 10</p><p className="big lp-display">6 days left</p>
                  </div>
                  <div className="lp-ledger-rows">
                    <div><div><p className="amt">{fmtMoney(M.current)}</p><p className="sub">current high bid</p></div><span className="lp-tag-lead">Leading</span></div>
                    <div style={{ opacity: 0.85 }}><div><p className="amt">{fmtMoney(M.opening)}</p><p className="sub">opening bid · sale confirmed</p></div><span className="sub">opening</span></div>
                    <div className="min"><div><p className="lp-mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".03em", color: "var(--accent)" }}>Minimum next bid</p><p className="lp-mono" style={{ fontSize: 10, color: "var(--muted)" }}>greater of 5% or $750</p></div><span className="amt" style={{ fontSize: 13, fontWeight: 600 }}>{fmtMoney(minNextUpset(M.current))}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* F03 — EQUITY */}
        <section id="sec-f03" className="lp-sec">
          <div className="lp-feat lp-reveal">
            <div className="lp-feat-copy">
              <p className="lp-kicker">03 — The math</p>
              <h2 className="lp-h2 lp-display">Equity at a glance, parcel by parcel.</h2>
              <p>Every parcel carries the numbers you actually bid on: county-assessed value, taxes and accrued costs owed, and the estimated equity spread. Sort a county by spread and surface the handful worth a drive-by in seconds.</p>
              <ul className="lp-feat-list">
                <li><span className="d">—</span> Assessed value from the county record, not guessed</li>
                <li><span className="d">—</span> Spread recomputed as each upset bid lands</li>
              </ul>
            </div>
            <div className="lp-feat-visual">
              <div className="lp-eqcard">
                <p className="pin">EXAMPLE · {M.address.toUpperCase()} · CHARLOTTE · PIN {M.parcel}</p>
                <div className="lp-eqcard-grid">
                  <div><p className="lab">Assessed</p><p className="big lp-display">{fmtMoney(M.assessed)}</p></div>
                  <div><p className="lab">Taxes owed</p><p className="big lp-display">{fmtMoney(M.taxesOwed)}</p></div>
                  <div><p className="lab acc">Est. equity</p><p className="big acc lp-display">{fmtMoney(equity(M))}</p></div>
                </div>
                <div className="lp-eqbar"><span className="t" style={{ width: "4.3%" }} /><span className="e" style={{ width: "95.7%" }} /></div>
                <div className="lp-eqbar-cap"><span>4.3% taxes owed</span><span>95.7% est. equity spread</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* F04 — OWNER LEADS */}
        <section id="sec-f04" className="lp-sec band">
          <div className="lp-feat rev lp-reveal" style={{ alignItems: "start" }}>
            <div className="lp-feat-copy">
              <p className="lp-kicker">04 — Owner leads</p>
              <h2 className="lp-h2 lp-display">Reach owners before the courthouse steps.</h2>
              <p>From the day the notice posts — often months before any sale — Parcely surfaces the owner's name and the address on file, and adds estimated equity wherever we hold the parcel record. Then it flags the ones worth a letter. Built for wholesalers and acquisitions investors.</p>
              <ul className="lp-feat-list">
                <li><span className="lp-lead-tag abs">Absentee</span> <span>Mailing address ≠ property — likelier to sell fast.</span></li>
                <li><span className="lp-lead-tag ent">Entity</span> <span>LLC- or trust-owned, with registered mailing surfaced.</span></li>
                <li><span className="d">—</span> Export a clean CSV straight into your mail-merge or CRM</li>
              </ul>
            </div>
            <div className="lp-feat-visual">
              <div className="lp-card">
                {/* A non-interactive <span>, NOT a <button>: this is an
                    illustrative card (fake rows), so a clickable-looking export
                    control is a false affordance — it did nothing on click and
                    misled people into expecting an action. As a span it keeps
                    the "CSV export is a feature" signal (it's real on Pro)
                    without the .lp button cursor:pointer / clickable semantics. */}
                <div className="lp-card-head"><span className="lp-mono">EXAMPLE · OFF-MARKET LEADS — MECKLENBURG</span><span className="lp-export" aria-hidden="true">↓ Export CSV</span></div>
                <div className="lp-table-scroll">
                <table className="lp-table">
                  <thead><tr><th>Owner</th><th>Mailing</th><th style={{ textAlign: "right" }}>Est. equity</th><th>Tags</th></tr></thead>
                  <tbody>
                    <tr><td><div style={{ fontWeight: 500 }}>Carolyn T. Pressley</div><div className="lp-muted" style={{ fontSize: 11 }}>{M.address}</div></td><td className="lp-muted lp-mono" style={{ fontSize: 11 }}>Sunset Beach, NC</td><td style={{ textAlign: "right", fontWeight: 500, color: "var(--accent)" }} className="lp-tnum">{fmtMoney(equity(M))}</td><td><span className="lp-lead-tag abs">Absentee</span></td></tr>
                    <tr><td><div style={{ fontWeight: 500 }}>Beacon Holdings LLC</div><div className="lp-muted" style={{ fontSize: 11 }}>{A.address}</div></td><td className="lp-muted lp-mono" style={{ fontSize: 11 }}>PO Box · Charlotte, NC</td><td style={{ textAlign: "right", fontWeight: 500, color: "var(--accent)" }} className="lp-tnum">{fmtMoney(equity(A))}</td><td><span className="lp-lead-tag ent">Entity</span></td></tr>
                    <tr><td><div style={{ fontWeight: 500 }}>Earl &amp; Doretha Sims</div><div className="lp-muted" style={{ fontSize: 11 }}>{D.address}</div></td><td className="lp-muted lp-mono" style={{ fontSize: 11 }}>Huntersville, NC</td><td style={{ textAlign: "right" }} className="lp-muted lp-tnum">{fmtMoney(equity(D))}</td><td><span className="lp-lead-tag abs">Absentee</span></td></tr>
                  </tbody>
                </table>
                </div>
              </div>
              <p className="lp-fineprint">Owner of record &amp; mailing pulled from the case file. Sample owner names shown.</p>
            </div>
          </div>
        </section>

        {/* F05 — WATCHLISTS (email, not SMS) */}
        <section id="sec-f05" className="lp-sec">
          <div className="lp-feat lp-reveal">
            <div className="lp-feat-copy">
              <p className="lp-kicker">05 — Watchlists</p>
              <h2 className="lp-h2 lp-display">Save a buy box. Come back to it.</h2>
              <p>Save a filter — Mecklenburg parcels under $20k owed with $100k+ assessed — and Parcely keeps it for you, re-run against a docket that refreshes twice a day.</p>
              <ul className="lp-feat-list">
                <li><span className="d">—</span> Watchlists and saved searches across all {coverageCount} counties</li>
                <li><span className="d">—</span> Email alerts when a watched property changes status or gets an upset bid</li>
              </ul>
            </div>
            <div className="lp-feat-visual">
              <div className="lp-card">
                <div className="lp-card-head band"><span className="lp-mono">EXAMPLE WATCHLIST — "MECK · UNDER $20K OWED"</span><span className="lp-mono">9 parcels</span></div>
                <div className="lp-alertrows">
                  <div style={{ background: "var(--tint)" }}><span className="ic acc">@</span><div><p className="t">Upset bid filed · {M.address}</p><p className="s">{fmtMoney(M.current)} · clock reset to 10 days · today</p></div></div>
                  <div><span className="ic n">@</span><div><p className="t">New notice of sale · {D.address}</p><p className="s">Opening bid {fmtMoney(D.opening)} · today</p></div></div>
                  <div><span className="ic w">!</span><div><p className="t">Sale continued · Cabarrus County</p><p className="s">Moved to a later sale date · today</p></div></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="sec-how" className="lp-sec band">
          <div className="lp-pad-lg">
            <div className="lp-reveal" style={{ maxWidth: 640 }}>
              <p className="lp-kicker">How it works</p>
              <h2 className="lp-h2 lp-display">Four steps, no CVD number required.</h2>
            </div>
            <div className="lp-grid4 lp-reveal">
              <div className="lp-cell"><p className="n">01</p><h3>Pick your county</h3><p>Choose from {coverageCount} parcel-enriched counties — and growing.</p></div>
              <div className="lp-cell"><p className="n">02</p><h3>Watch the notices</h3><p>Foreclosure, estate, tax and HOA filings in one feed, refreshed twice a day.</p></div>
              <div className="lp-cell"><p className="n">03</p><h3>Work the addressed ones</h3><p>Filter to leads that carry an address, then mail, call or drive them.</p></div>
              <div className="lp-cell"><p className="n">04</p><h3>Or bid at the sale</h3><p>If it reaches auction, you already know the parcel and the upset clock.</p></div>
            </div>
          </div>
        </section>

        {/* COVERAGE */}
        <section id="sec-coverage" className="lp-sec">
          <div className="lp-pad-lg lp-reveal">
            <div className="lp-cov-head">
              <div>
                <p className="lp-kicker">Coverage</p>
                <h2 className="lp-h2 lp-display" style={{ maxWidth: "12em" }}>{coverageCount} NC counties at full parcel depth — and growing.</h2>
              </div>
              <p className="lp-mono lp-muted" style={{ fontSize: 11 }}>Live today · more added monthly</p>
            </div>
            {/* Was 11 individually hardcoded spans, none of which named
                Guilford, Wake, or Cumberland — the exact three counties the
                FAQ answer above (buildFaqs, "Which counties do you cover?")
                names as anchors alongside Mecklenburg, Rowan and Gaston. A
                visitor reading that FAQ then scrolling here saw their county
                missing from a page that had just claimed it was covered.
                Now driven from the real COUNTIES array in landingData.ts,
                so this list and the FAQ text can't drift apart again. */}
            <div className="lp-chips">
              {coverageCounties.map((c, i) => (
                <a
                  key={c.slug}
                  href={`/public/${c.slug}`}
                  className={i === 0 ? "lp-chip first" : "lp-chip"}
                >
                  {c.name}
                </a>
              ))}
              <span className="lp-chip more">+ more soon</span>
            </div>
            <p className="lp-fineprint" style={{ maxWidth: "58em" }}>
              Those are the counties where a lead arrives with the parcel record attached — assessed value, equity,
              and upset-bid tracking. The legal-notice feed reaches beyond them, but that coverage is thin and
              uneven from county to county, so treat it as reach, not depth.
            </p>
          </div>
        </section>

        {/* ALERTS (was SMS — now email). Built but not delivering — 0 sends
            since 2026-07-21 — so this section is deliberately framed as
            what's COMING, not a mockup of a live inbox. The previous version
            showed a "PARCELY ALERTS / Email · now" phone screenshot with
            three fabricated delivered messages; that shipped a false claim
            in the most visually persuasive way possible on the page. */}
        <section id="sec-sms" className="lp-sec band">
          <div className="lp-feat lp-reveal">
            <div className="lp-feat-copy">
              <p className="lp-kicker">Alerts</p>
              <h2 className="lp-h2 lp-display">Your watchlist emails you.</h2>
              <p>When a property you're watching changes status, gets a new upset bid, or its window is about to close, Parcely sends you an email, and a weekly digest rounds up the week. Alerts for brand-new listings in a county are not on yet.</p>
            </div>
            <div className="lp-feat-visual" style={{ display: "flex", justifyContent: "center" }}>
              <div className="lp-phone">
                <div className="lp-phone-inner">
                  <div className="lp-phone-bar"><p className="l1">INBOX</p><p className="l2">Example emails</p></div>
                  <div className="lp-phone-body">
                    <div className="lp-phone-msg"><p className="m1">Parcely Alerts · <b>New upset bid on {M.address}</b></p><p className="m2">Bid is now {fmtMoney(M.current)}. The upset window is still open.</p></div>
                    <div className="lp-phone-msg"><p className="m1">Parcely Alerts · <b>Status changed: {D.address}</b></p><p className="m2">Now an auction with an opening bid of {fmtMoney(D.opening)}.</p></div>
                    <div className="lp-phone-msg"><p className="m1">Parcely Alerts · <b>Sale postponed in Cabarrus Co.</b></p><p className="m2">Moved to a later sale date.</p></div>
                    <p className="lp-phone-foot">Illustrative examples with sample properties</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* COMPARISON */}
        <section id="sec-compare" className="lp-sec">
          <div className="lp-pad-lg lp-reveal">
            <div style={{ maxWidth: 640 }}>
              <p className="lp-kicker">Why Parcely</p>
              <h2 className="lp-h2 lp-display">What the clerk's site won't do for you.</h2>
            </div>
            <div className="lp-cmp">
              <table>
                <thead><tr><th></th><th><span className="me lp-display">Parcely</span></th><th><span className="them">eCourts / clerk</span></th><th><span className="them">National sites</span></th></tr></thead>
                <tbody>
                  {COMPARE.map(([label, p, e, n]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className="me">{p ? <Check /> : <span className="dash">—</span>}</td>
                      <td>{e ? <Check /> : <span className="dash">—</span>}</td>
                      <td>{n ? <Check /> : <span className="dash">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* THE RECORD (genericized) */}
        <section id="sec-record" className="lp-sec band">
          <div className="lp-pad-lg">
            <div className="lp-reveal" style={{ maxWidth: 640 }}>
              <p className="lp-kicker">Sourced from the record</p>
              <h2 className="lp-h2 lp-display">Credible only if the data is.</h2>
            </div>
            <div className="lp-grid3 lp-reveal">
              <div className="lp-cell lg"><p className="n">01</p><h3>Pull from the record</h3><p>Legal notices, sale notices and upset filings read from county court and public records — the same documents you'd verify by hand.</p></div>
              <div className="lp-cell lg"><p className="n">02</p><h3>Match &amp; normalize</h3><p>Where a parcel match exists, the case is joined to the county record and assessed value, so an address becomes a mapped boundary with equity attached. Where it doesn't, we say so rather than guess.</p></div>
              <div className="lp-cell lg"><p className="n">03</p><h3>Compute &amp; monitor</h3><p>We recompute equity, watch for upset resets and continuances twice a day, and push changes to your watchlists.</p></div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="sec-faq" className="lp-sec">
          <div className="lp-faq-grid lp-reveal">
            <div>
              <p className="lp-kicker">FAQ</p>
              <h2 className="lp-h2 lp-display" style={{ fontSize: "clamp(28px,3.4vw,36px)" }}>Before you trust it with a mailer or a bid.</h2>
              <p className="lp-lede" style={{ fontSize: 14 }}>Still have a question? <a className="lp-faq-link" href="mailto:hello@parcelyapp.com">Email me</a> — a person who knows NC foreclosure and probate answers.</p>
            </div>
            <div className="lp-faq-list">
              {faqs.map(([q, a]) => (
                <details className="lp-faq" key={q}>
                  <summary><span className="lp-faq-q">{q}</span><span className="lp-faq-sign">+</span></summary>
                  <p className="lp-faq-a">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING PREVIEW */}
        <section id="sec-pricing" className="lp-sec band">
          <div className="lp-pricing lp-reveal">
            <p className="lp-kicker">Pricing</p>
            <p className="lp-pricing-tag lp-display">One good deal pays for a decade of Parcely.</p>
            <p className="lp-pricing-line">Free to browse the feed. <span className="acc">Scout $45/mo</span> for 3 counties you choose. <span className="acc">Pro $75/mo</span> for leads, exports &amp; the full auction calendar statewide.</p>
            <a href="/pricing" className="lp-btn lp-btn-outline" style={{ marginTop: 28 }}>See full pricing</a>
          </div>
        </section>

        {/* FINAL CTA */}
        <section id="sec-cta" className="lp-sec tint">
          <div className="lp-final lp-reveal">
            <h2 className="lp-display">See this week's NC off-market leads in your buy box.</h2>
            <div className="lp-cta-row center" style={{ marginTop: 32 }}>
              <button className="lp-btn lp-btn-primary" onClick={onGetStarted} style={{ padding: "14px 28px" }}>Get started — free</button>
            </div>
            <div className="lp-expand">
              <p className="lp-expand-lab">Not in your county yet? Tell me where to expand.</p>
              <form className="lp-expand-form" onSubmit={handleExpand}>
                <input type="email" required placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email for expansion updates" />
                <button type="submit">Notify me</button>
              </form>
              <p className="lp-expand-msg">{expandMsg}</p>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER — shared editorial chrome (same footer on every public page) */}
      <PublicPageFooter />
    </div>
  );
};

function Check() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2E5A88" strokeWidth="2" style={{ display: "inline-block" }} aria-label="yes"><path d="M3 8.5l3.2 3.2L13 4.5" /></svg>
  );
}

export default LandingPage;
